<template>
  <a-input-group compact :class="[bem.b('container')]">
    <ll-input
      v-if="!pickerMode"
      v-model:value="customInput"
      :size="size"
      :disabled="colorDisabled"
      :readonly="readonly"
      :allow-clear="allowClear"
      :placeholder="placeholder"
      @clear="clear"
      @change="handleChange"
    />
    <a-popover :open="showPicker" trigger="click" v-bind="$attrs">
      <template #content>
        <div :class="bem.m('dropdown-main-wrapper')">
          <sv-panel ref="svPanel" :color="color" />
          <hue-slider
            ref="hueSlider"
            class="hue-slider"
            :color="color"
            vertical
          />
        </div>
        <alpha-slider v-if="showAlpha" ref="alpha" :color="color" />
        <pre-define
          v-if="predefine.length"
          ref="predefine"
          :color="color"
          :colors="predefine"
        />
        <div :class="bem.m('dropdown-button')">
          <span :class="bem.m('dropdown-value')">
            <ll-button
              v-if="props.usePipette"
              size="small"
              @click="handleColorPicking"
            >
              <ll-icon icon-name="icon-straw" />
            </ll-button>
            <ll-input
              v-model:value="customInput"
              size="small"
              @press-enter="handleConfirm"
              @blur="handleConfirm"
            />
          </span>
          <div :class="bem.m('button')">
            <ll-button
              type="link"
              size="small"
              :class="bem.m('cancel-button')"
              @click="clear"
            >
              清空
            </ll-button>
            <ll-button size="small" @click="confirmValue"> 确定 </ll-button>
          </div>
        </div>
      </template>
      <ll-button
        :class="bem.b()"
        :disabled="colorDisabled"
        :size="size"
        @click="handleTrigger"
      >
        <template #icon>
          <div
            :class="[
              bem.m('color'),
              showAlpha ? bem.is('alpha') : '',
              !pickerMode ? bem.is('show-input') : '',
            ]"
          >
            <div
              :class="[bem.m('color-inner')]"
              :style="{ backgroundColor: displayedColor }"
            >
              <ll-icon
                :icon-name="
                  showPicker
                    ? 'icon-line-arrow-folding'
                    : 'icon-line-arrow-down'
                "
                color="var(--default-icon)"
                :size="size === 'small' ? 12 : undefined"
              />
            </div>
          </div>
        </template>
      </ll-button>
    </a-popover>
  </a-input-group>
</template>
<script lang="ts" setup>
// Utils
import { createNamespace } from '@ll-plus/utils'
import { useDebounceFn } from '@vueuse/core'
import { Form } from 'ant-design-vue'
import {
  computed,
  nextTick,
  onMounted,
  provide,
  reactive,
  ref,
  watch,
} from 'vue'
import {
  OPTIONS_KEY,
  colorPickerEmits,
  colorPickerProps,
  displayedRgb,
  type IUseOptions,
} from './config'
import Color from './config/color'
import ColorPipette from './config/color-pipette'

// Components
import AlphaSlider from './components/alpha-slider.vue'
import HueSlider from './components/hue-slider.vue'
import PreDefine from './components/pre-define.vue'
import SvPanel from './components/sv-panel.vue'

import type { IProps } from './config/color-pipette/types'

// Options/Props/Emits
defineOptions({ name: 'LlColorPicker' })
const props = defineProps(colorPickerProps)
const emits = defineEmits(colorPickerEmits)
const bem = createNamespace('color-picker')

//Data
const hasClickBtn = ref<boolean>(false)
const hueSlider = ref<InstanceType<typeof HueSlider>>()
const svPanel = ref<InstanceType<typeof SvPanel>>()
const alpha = ref<InstanceType<typeof AlphaSlider>>()
const color = reactive<Color>(
  new Color({
    enableAlpha: props.showAlpha,
    format: props.colorFormat,
  }),
) as Color
const pipette = reactive<ColorPipette>(
  new ColorPipette({
    container: props.pipetteContainer,
    colorFormat: props.colorFormat,
    listener: {
      onOk: async (e) => {
        console.log({
          color: e.color,
          colors: e.colors,
          container: props.pipetteContainer,
        })
        color.fromString(e.color)
        customInput.value = e.color
        // await confirmValue()
      },
    },
  } as IProps),
) as ColorPipette
const showPicker = ref<boolean>(false)
const showPanelColor = ref(false)
const customInput = ref('')
const formItemContext = Form.useInjectFormItemContext()
// computed
const displayedColor = computed(() => {
  if (!props.value && !showPanelColor.value) {
    return 'transparent'
  }
  return displayedRgb(color, props.showAlpha)
})

const colorDisabled = computed(() => {
  return props.disabled
})
const currentColor = computed(() => {
  return !props.value && !showPanelColor.value ? '' : color.value
})

provide<IUseOptions>(OPTIONS_KEY, {
  currentColor,
})

// Methods

const setShowPicker = (value: boolean) => {
  showPicker.value = value
}

const debounceSetShowPicker = useDebounceFn(setShowPicker, 50)
// 关闭颜色选择弹窗
const hide = () => {
  debounceSetShowPicker(false)
  resetColor()
}
const resetColor = () => {
  nextTick(() => {
    if (props.value) {
      color.fromString(props.value)
    } else {
      showPanelColor.value = false
    }
  })
}
const handleTrigger = () => {
  if (colorDisabled.value) return
  debounceSetShowPicker(!showPicker.value)
}
const handleConfirm = () => {
  // hasClickBtn.value = true
  color.fromString(customInput.value)
}
const confirmValue = () => {
  hasClickBtn.value = true
  const value = color.value
  emits('update:value', value)
  emits('change', value)
  formItemContext.onFieldChange()
  debounceSetShowPicker(false)
  nextTick(() => {
    const newColor = new Color({
      enableAlpha: props.showAlpha,
      format: props.colorFormat,
    })
    newColor.fromString(props.value)
    if (!color.compare(newColor)) {
      resetColor()
    }
  })
  showPicker.value = false
}
const clear = () => {
  hasClickBtn.value = true
  debounceSetShowPicker(false)
  emits('update:value', null)
  emits('change', null)
  formItemContext.onFieldChange()
  resetColor()
  showPicker.value = false
}

const handleChange = (e: any) => {
  hasClickBtn.value = true
  emits('update:value', e.target.value)
  emits('change', e.target.value)
  formItemContext.onFieldChange()
  nextTick(() => {
    const newColor = new Color({
      enableAlpha: props.showAlpha,
      format: props.colorFormat,
    })
    newColor.fromString(props.value)
    if (!color.compare(newColor)) {
      resetColor()
    }
  })
  showPicker.value = false
}

const getPopupContainer = (triggerNode: HTMLElement) => {
  return triggerNode.parentElement
}

const handleColorPicking = async () => {
  // if (window.EyeDropper) {
  //   // 如果支持原生API，使用原生API
  //   try {
  //     const eyeDropper = new window.EyeDropper()
  //     const result = await eyeDropper.open()
  //     color.fromString(result.sRGBHex)
  //     customInput.value = result.sRGBHex
  //     return
  //   } catch (e) {
  //     console.log('用户取消了取色')
  //     return
  //   }
  // }
  pipette.start()
}

watch(
  () => props.value,
  (newVal) => {
    if (!newVal) {
      showPanelColor.value = false
    } else if (newVal && newVal !== color.value) {
      color.fromString(newVal)
    }
  },
)
watch(
  () => currentColor.value,
  (val) => {
    customInput.value = val ? val : ''
    emits('active-change', val)
  },
)
watch(
  () => color.value,
  () => {
    if (!props.value && !showPanelColor.value) {
      showPanelColor.value = true
    }
  },
)
watch(
  () => showPicker.value,
  (newVal) => {
    if (newVal) {
      hasClickBtn.value = false
    } else {
      if (!hasClickBtn.value) {
        hide()
      }
    }
    nextTick(() => {
      hueSlider.value?.update()
      setTimeout(() => {
        svPanel.value?.update()
        alpha.value?.update()
      }, 20)
    })
  },
)

watch([() => props.colorFormat, () => props.showAlpha], ([val, alpha]) => {
  const newColor = new Color({
    enableAlpha: alpha,
    format: val,
  })
  for (const i in newColor) {
    if (
      Object.prototype.hasOwnProperty.call(color, i) &&
      Object.prototype.hasOwnProperty.call(newColor, i)
    ) {
      color[i] = newColor[i]
    }
  }
  pipette.setColorFormat(val)
  confirmValue()
})

onMounted(() => {
  if (props.value) {
    color.fromString(props.value)
    customInput.value = currentColor.value
  }
})

defineExpose({
  getPopupContainer,
  setShowPicker,
  debounceSetShowPicker,
})
</script>
